import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaFileAlt,
  FaCog,
  FaShieldAlt,
  FaChartLine,
  FaRocket,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

export default function Features() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/signup');
  };

  const handleScheduleDemo = () => {
    navigate('/support');
  };

  const features = [
    {
      id: 1,
      icon: FaUsers,
      title: "Expand your customer base",
      description:
        "Access thousands of new clients daily. Reach new markets and grow your business exponentially with our extensive network.",
    },
    {
      id: 2,
      icon: FaFileAlt,
      title: "Automated policy issuance",
      description:
        "Streamline your operations with instant policy generation. Reduce manual work and process claims faster with our automated systems.",
    },
    {
      id: 3,
      icon: FaCog,
      title: "Flexible integration options",
      description:
        "Seamlessly integrate with your existing systems. Choose from API, webhooks, or our dashboard to fit your workflow perfectly.",
    },
    {
      id: 4,
      icon: FaShieldAlt,
      title: "Advanced risk management",
      description:
        "Leverage our AI-powered risk assessment tools. Make data-driven decisions and optimize your underwriting process.",
    },
    {
      id: 5,
      icon: FaChartLine,
      title: "Real-time analytics",
      description:
        "Monitor performance with comprehensive dashboards. Track sales, claims, and customer behavior in real-time.",
    },
    {
      id: 6,
      icon: FaRocket,
      title: "Rapid market entry",
      description:
        "Launch new products quickly with our pre-built templates. Get to market faster than traditional insurance platforms.",
    },
  ];

  const scrollToCard = (index) => {
    if (carouselRef.current) {
      const cardWidth = 320; // w-80 = 320px
      const gap = 24; // gap-6 = 24px
      const scrollPosition = index * (cardWidth + gap);
      
      carouselRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };

  const scrollNext = () => {
    const nextIndex = Math.min(currentIndex + 1, features.length - 1);
    scrollToCard(nextIndex);
  };

  const scrollPrev = () => {
    const prevIndex = Math.max(currentIndex - 1, 0);
    scrollToCard(prevIndex);
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const cardWidth = 320;
      const gap = 24;
      const newIndex = Math.round(scrollLeft / (cardWidth + gap));
      setCurrentIndex(newIndex);
    }
  };

  return (
    <section className="px-4 py-20 bg-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <h2 className="mb-6 text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl">
          Join the Future of {" "}
            <span className="px-2 py-1 bg-green-100 rounded-lg">
            Insurance Distribution
            </span>
          </h2>
          <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600">
            Join the future of insurance distribution. Our platform empowers insurance
            providers to reach more customers, streamline operations, and grow
            their business with cutting-edge technology and comprehensive
            support.
          </p>
        </motion.div>

        {/* Features Carousel */}
        <div className="relative overflow-hidden">
          {/* Navigation Arrows */}
          <button
            onClick={scrollPrev}
            disabled={currentIndex === 0}
            className="flex absolute left-2 top-1/2 z-10 justify-center items-center w-12 h-12 text-gray-600 bg-white rounded-full border border-gray-200 shadow-lg transition-all duration-300 -translate-y-1/2 hover:text-green-600 hover:border-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaChevronLeft size={20} />
          </button>

          <button
            onClick={scrollNext}
            disabled={currentIndex === features.length - 1}
            className="flex absolute right-2 top-1/2 z-10 justify-center items-center w-12 h-12 text-gray-600 bg-white rounded-full border border-gray-200 shadow-lg transition-all duration-300 -translate-y-1/2 hover:text-green-600 hover:border-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaChevronRight size={20} />
          </button>

          {/* Carousel Container */}
          <div 
            ref={carouselRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto gap-6 pb-8 scrollbar-hide scroll-smooth"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex-shrink-0 w-80 lg:w-96"
              >
                {/* Feature Card */}
                <div className="p-8 h-full bg-white rounded-2xl border border-gray-100 shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                  {/* Icon */}
                  <div className="flex justify-center items-center mb-6 w-16 h-16 bg-green-500 rounded-full">
                    <feature.icon size={24} className="text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="mb-4 text-xl font-bold leading-tight text-gray-900">
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="leading-relaxed text-gray-600">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Scroll Indicators */}
          <div className="flex justify-center mt-8">
            <div className="flex space-x-2">
              {features.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToCard(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                    index === currentIndex ? "bg-green-500" : "bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center px-6 py-3 mb-6 text-green-700 bg-green-50 rounded-full">
            <FaRocket size={20} className="mr-2" />
            <span className="font-medium">
              Ready to transform your business?
            </span>
          </div>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <button 
              onClick={handleGetStarted}
              className="px-8 py-4 font-semibold text-white bg-green-600 rounded-full shadow-lg transition-all duration-300 hover:bg-green-700 hover:scale-105"
            >
              Get Started Today
            </button>
            <button 
              onClick={handleScheduleDemo}
              className="px-8 py-4 font-semibold text-green-600 rounded-full border-2 border-green-600 transition-all duration-300 hover:bg-green-50"
            >
              Schedule Demo
            </button>
          </div>
        </motion.div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scroll-smooth {
          scroll-behavior: smooth;
        }
      `}</style>
    </section>
  );
}
